/**
 * NeuralNews Admin UI — i18n (Internationalisation) Hook
 *
 * Provides a lightweight translation system for the React admin dashboard.
 * Uses the backend's /api/i18n/catalogue endpoint for server-managed strings
 * and falls back to bundled English defaults.
 *
 * Usage:
 *   import { useI18n, I18nProvider } from '../hooks/use-i18n';
 *
 *   // Wrap app with provider:
 *   <I18nProvider locale="fr">
 *     <App />
 *   </I18nProvider>
 *
 *   // In components:
 *   const { t, locale, setLocale, availableLocales } = useI18n();
 *   <h1>{t('dashboard.title')}</h1>
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// ── Default English strings ──────────────────────────────────────────
const EN_MESSAGES = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.articles': 'Articles',
  'nav.media': 'Media Library',
  'nav.categories': 'Categories',
  'nav.comments': 'Comments',
  'nav.users': 'Users',
  'nav.analytics': 'Analytics',
  'nav.settings': 'Settings',
  'nav.wire_feeds': 'Wire Feeds',
  'nav.newsletters': 'Newsletters',
  'nav.social': 'Social Publishing',
  'nav.hr': 'HR',
  'nav.finance': 'Finance',
  'nav.elections': 'Elections',
  'nav.field_ops': 'Field Operations',

  // Dashboard
  'dashboard.title': 'Dashboard',
  'dashboard.welcome': 'Welcome back',
  'dashboard.total_articles': 'Total Articles',
  'dashboard.published': 'Published',
  'dashboard.drafts': 'Drafts',
  'dashboard.total_users': 'Total Users',
  'dashboard.total_views': 'Total Views',
  'dashboard.recent_activity': 'Recent Activity',

  // Articles
  'articles.title': 'Articles',
  'articles.new': 'New Article',
  'articles.edit': 'Edit Article',
  'articles.delete': 'Delete',
  'articles.publish': 'Publish',
  'articles.schedule': 'Schedule',
  'articles.save_draft': 'Save Draft',
  'articles.status': 'Status',
  'articles.author': 'Author',
  'articles.category': 'Category',
  'articles.tags': 'Tags',
  'articles.featured_image': 'Featured Image',
  'articles.views': 'Views',
  'articles.created_at': 'Created',
  'articles.updated_at': 'Updated',
  'articles.no_articles': 'No articles found',
  'articles.search_placeholder': 'Search articles…',
  'articles.lock': 'Lock',
  'articles.unlock': 'Unlock',
  'articles.approve': 'Approve',
  'articles.reject': 'Reject',

  // Common actions
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.create': 'Create',
  'common.search': 'Search',
  'common.filter': 'Filter',
  'common.export': 'Export',
  'common.import': 'Import',
  'common.loading': 'Loading…',
  'common.error': 'An error occurred',
  'common.success': 'Success',
  'common.confirm': 'Confirm',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.showing': 'Showing',
  'common.of': 'of',
  'common.results': 'results',
  'common.no_data': 'No data available',
  'common.refresh': 'Refresh',
  'common.close': 'Close',

  // Auth
  'auth.login': 'Log In',
  'auth.logout': 'Log Out',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.forgot_password': 'Forgot password?',
  'auth.sso_login': 'Log in with SSO',

  // Wire feeds
  'wire.title': 'Wire Feed Management',
  'wire.poll_all': 'Poll All Feeds',
  'wire.add_feed': 'Add Feed',
  'wire.items': 'Wire Items',
  'wire.import': 'Import as Draft',
  'wire.discard': 'Discard',

  // Settings
  'settings.title': 'Settings',
  'settings.general': 'General',
  'settings.theme': 'Theme',
  'settings.language': 'Language',
  'settings.notifications': 'Notifications',
  'settings.api_keys': 'API Keys',
  'settings.sso': 'Single Sign-On',
  'settings.backup': 'Backup & Restore',
};

// ── Supported locales with display names ─────────────────────────────
const AVAILABLE_LOCALES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'sw', name: 'Kiswahili', flag: '🇰🇪' },
];

// ── Context ──────────────────────────────────────────────────────────
const I18nContext = createContext(null);

/**
 * I18nProvider — wraps the app and provides translation functions.
 *
 * @param {string} locale - Initial locale code (default: 'en')
 * @param {string} apiBaseUrl - Backend API base URL for fetching catalogues
 */
export function I18nProvider({ children, locale: initialLocale = 'en', apiBaseUrl = '/api' }) {
  const [locale, setLocaleState] = useState(() => {
    // Try to restore from localStorage
    const saved = typeof window !== 'undefined' ? localStorage.getItem('nn_locale') : null;
    return saved || initialLocale;
  });

  const [messages, setMessages] = useState({ en: EN_MESSAGES });
  const [loading, setLoading] = useState(false);

  // Fetch translations from backend
  const fetchCatalogue = useCallback(async (lang) => {
    if (lang === 'en' || messages[lang]) return; // Already have it

    setLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/i18n/catalogue/${lang}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => ({ ...prev, [lang]: data.messages || data }));
      }
    } catch (err) {
      console.warn(`[i18n] Failed to fetch ${lang} catalogue:`, err);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, messages]);

  const setLocale = useCallback((newLocale) => {
    setLocaleState(newLocale);
    if (typeof window !== 'undefined') {
      localStorage.setItem('nn_locale', newLocale);
      // Set document direction for RTL languages
      const localeInfo = AVAILABLE_LOCALES.find(l => l.code === newLocale);
      document.documentElement.dir = localeInfo?.dir || 'ltr';
      document.documentElement.lang = newLocale;
    }
    fetchCatalogue(newLocale);
  }, [fetchCatalogue]);

  // Load initial locale catalogue
  useEffect(() => {
    if (locale !== 'en') {
      fetchCatalogue(locale);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Translate a key. Supports simple interpolation with {{var}} syntax.
   *
   * @param {string} key - Dot-separated message key (e.g. 'dashboard.title')
   * @param {Object} vars - Interpolation variables (e.g. { name: 'John' })
   * @returns {string}
   */
  const t = useCallback((key, vars = {}) => {
    const localeMsgs = messages[locale] || {};
    let msg = localeMsgs[key] || EN_MESSAGES[key] || key;

    // Interpolate {{var}} placeholders
    if (vars && typeof msg === 'string') {
      Object.entries(vars).forEach(([k, v]) => {
        msg = msg.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
      });
    }

    return msg;
  }, [locale, messages]);

  /**
   * Format a number using the current locale.
   */
  const formatNumber = useCallback((num) => {
    try {
      return new Intl.NumberFormat(locale).format(num);
    } catch {
      return String(num);
    }
  }, [locale]);

  /**
   * Format a date using the current locale.
   */
  const formatDate = useCallback((date, options = {}) => {
    try {
      const d = date instanceof Date ? date : new Date(date);
      return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        ...options,
      }).format(d);
    } catch {
      return String(date);
    }
  }, [locale]);

  /**
   * Relative time formatting (e.g. "3 hours ago")
   */
  const formatRelative = useCallback((date) => {
    try {
      const d = date instanceof Date ? date : new Date(date);
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      const diffMs = d - new Date();
      const diffSec = Math.round(diffMs / 1000);
      const diffMin = Math.round(diffSec / 60);
      const diffHr = Math.round(diffMin / 60);
      const diffDay = Math.round(diffHr / 24);

      if (Math.abs(diffDay) >= 1) return rtf.format(diffDay, 'day');
      if (Math.abs(diffHr) >= 1) return rtf.format(diffHr, 'hour');
      if (Math.abs(diffMin) >= 1) return rtf.format(diffMin, 'minute');
      return rtf.format(diffSec, 'second');
    } catch {
      return String(date);
    }
  }, [locale]);

  const value = useMemo(() => ({
    t,
    locale,
    setLocale,
    loading,
    availableLocales: AVAILABLE_LOCALES,
    formatNumber,
    formatDate,
    formatRelative,
    isRTL: AVAILABLE_LOCALES.find(l => l.code === locale)?.dir === 'rtl',
  }), [t, locale, setLocale, loading, formatNumber, formatDate, formatRelative]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Hook to access translation functions.
 *
 * @returns {{ t, locale, setLocale, loading, availableLocales, formatNumber, formatDate, formatRelative, isRTL }}
 */
export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    // Fallback for components outside the provider — return English defaults
    return {
      t: (key, vars) => {
        let msg = EN_MESSAGES[key] || key;
        if (vars) {
          Object.entries(vars).forEach(([k, v]) => {
            msg = msg.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
          });
        }
        return msg;
      },
      locale: 'en',
      setLocale: () => {},
      loading: false,
      availableLocales: AVAILABLE_LOCALES,
      formatNumber: (n) => String(n),
      formatDate: (d) => String(d),
      formatRelative: (d) => String(d),
      isRTL: false,
    };
  }
  return context;
}

export default useI18n;
